/**
 * Token-bound formula card for monograph demo panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { MonographAccentCard } from "./MonographAccentCard.js";
import type { MonographPanelTokens } from "./MonographPanelPrimitiveTypes.js";

export interface MonographFormulaCardProps {
  readonly tokens: MonographPanelTokens;
  readonly symbol?: React.ReactNode;
  readonly title: React.ReactNode;
  readonly formula?: React.ReactNode;
  readonly children?: React.ReactNode;
  readonly example?: React.ReactNode;
  readonly accent?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
}

export function MonographFormulaCard({
  tokens,
  symbol,
  title,
  formula,
  children,
  example,
  accent = tokens.accent,
  className,
  style,
}: MonographFormulaCardProps): React.JSX.Element {
  return (
    <MonographAccentCard
      tokens={tokens}
      symbol={symbol}
      title={title}
      accent={accent}
      contentStyle={{ display: "flex", flexDirection: "column", gap: 8 }}
      {...(className === undefined ? {} : { className })}
      {...(style === undefined ? {} : { style })}
    >
      {formula ? (
        <div
          style={{
            background: "rgba(4,6,15,0.55)",
            border: `1px solid ${tokens.border}`,
            borderRadius: 8,
            color: tokens.text,
            fontFamily: tokens.mono,
            fontSize: 13,
            padding: "8px 10px",
          }}
        >
          {formula}
        </div>
      ) : null}
      {children ? (
        <div style={{ color: tokens.text, fontSize: 12.5, lineHeight: 1.55 }}>{children}</div>
      ) : null}
      {example ? (
        <div style={{ color: accent, fontFamily: tokens.mono, fontSize: 11.5 }}>{example}</div>
      ) : null}
    </MonographAccentCard>
  );
}
